import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  isContaGatewayEfi,
  isContaRepasse,
  ordenarContasRepasse,
} from "../types/contas-financeiras.types";
import type { UseContasFinanceirasGerencialResult } from "../hooks/useContasFinanceirasGerencial";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";
import { mensagemConciliacaoSaldoEfi } from "../helpers/contas-financeiras-saldo-efi";
import {
  CONTAS_FIN_ACCENT,
  contasFinToolbarSx,
} from "../../components/contas-financeiras.theme";
import FinanceiroErrorState from "./FinanceiroErrorState";
import ContaFinanceiraCard from "./ContaFinanceiraCard";
import ContaFinanceiraFormDialog from "./ContaFinanceiraFormDialog";
import ExcluirContaFinanceiraDialog from "./ExcluirContaFinanceiraDialog";
import TransferenciaContaFinanceiraDialog from "./TransferenciaContaFinanceiraDialog";
import ConciliarSaldoContaDialog from "./ConciliarSaldoContaDialog";

const gridCardItemSx = {
  display: "flex",
  alignItems: "stretch",
  "& > *": { width: "100%" },
};

type Props = {
  gerencial: UseContasFinanceirasGerencialResult;
};

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={accent ? CONTAS_FIN_ACCENT : "text.primary"}
        noWrap
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function SectionToolbar({ title }: { title: string }) {
  return (
    <MDBox sx={contasFinToolbarSx}>
      <Stack direction="row" spacing={1} alignItems="center">
        <AccountBalanceWalletOutlinedIcon sx={{ color: CONTAS_FIN_ACCENT }} />
        <MDTypography variant="button" fontWeight="medium" color="text">
          {title}
        </MDTypography>
      </Stack>
    </MDBox>
  );
}

function CardAdicionar({ onClick }: { onClick: () => void }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      sx={{
        height: "100%",
        minHeight: 220,
        borderRadius: 2,
        border: "2px dashed",
        borderColor: "rgba(249, 96, 48, 0.35)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        bgcolor: "rgba(249, 96, 48, 0.02)",
        transition: "border-color 0.2s, background-color 0.2s",
        "&:hover": {
          borderColor: CONTAS_FIN_ACCENT,
          bgcolor: "rgba(249, 96, 48, 0.06)",
        },
      }}
    >
      <AddOutlinedIcon sx={{ fontSize: 40, color: CONTAS_FIN_ACCENT, mb: 1 }} />
      <Typography variant="subtitle2" fontWeight={700}>
        Adicionar conta
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.5, px: 2, textAlign: "center" }}
      >
        Nova conta bancária, caixa ou gateway
      </Typography>
    </Box>
  );
}

export default function ContasFinanceirasSection({ gerencial }: Props) {
  const { data, loading, error, refetch } = gerencial;

  const contaEfi = data?.contas.find((c) => isContaGatewayEfi(c) && c.ativo);
  const contasRepasse = ordenarContasRepasse(
    data?.contas.filter((c) => c.ativo && isContaRepasse(c)) ?? [],
  );
  const outrasContas =
    data?.contas.filter(
      (c) => c.ativo && !isContaGatewayEfi(c) && !isContaRepasse(c),
    ) ?? [];
  const contasInativas = data?.contas.filter((c) => !c.ativo) ?? [];
  const podeTransferir = (data?.contas.filter((c) => c.ativo).length ?? 0) >= 2;

  const totalDestaque = (contaEfi ? 1 : 0) + contasRepasse.length;

  const contaOrigemTransferencia =
    data?.contas.find((c) => c.id === gerencial.transferenciaDialog.contaOrigem?.id) ??
    gerencial.transferenciaDialog.contaOrigem;
  const contaConciliacao =
    data?.contas.find((c) => c.id === gerencial.conciliacaoDialog.conta?.id) ??
    gerencial.conciliacaoDialog.conta;

  const cardRepasseProps = {
    destaque: "repasse" as const,
    onEditar: gerencial.abrirEditar,
    onMarcarPadrao: gerencial.marcarPadrao,
    onAlternarAtivo: gerencial.alternarAtivo,
    onTransferir: podeTransferir ? gerencial.abrirTransferencia : undefined,
    onConciliarSaldo: gerencial.abrirConciliacao,
    onExcluir: gerencial.abrirExcluir,
  };

  const saldoEfi = data?.saldoDisponivelEfi ?? contaEfi?.saldoEfiDisponivel ?? null;
  const saldoCaixa = data?.saldoCaixaBancos ?? 0;
  const statsLoading = loading && !data;

  return (
    <Stack spacing={3}>
      {gerencial.conciliacaoFeedback?.aplicada ? (
        <Alert severity="success" onClose={gerencial.limparConciliacaoFeedback}>
          {mensagemConciliacaoSaldoEfi(gerencial.conciliacaoFeedback)}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          {statsLoading ? (
            <Skeleton variant="rounded" height={72} sx={{ borderRadius: 2 }} />
          ) : (
            <StatMini
              label="Disponível na Efí"
              value={saldoEfi != null ? formatarMoedaBRL(saldoEfi) : "—"}
              accent
            />
          )}
        </Grid>
        <Grid item xs={6} sm={3}>
          {statsLoading ? (
            <Skeleton variant="rounded" height={72} sx={{ borderRadius: 2 }} />
          ) : (
            <StatMini
              label="Caixa / bancos"
              value={formatarMoedaBRL(saldoCaixa)}
            />
          )}
        </Grid>
        <Grid item xs={6} sm={3}>
          {statsLoading ? (
            <Skeleton variant="rounded" height={72} sx={{ borderRadius: 2 }} />
          ) : (
            <StatMini
              label="Contas ativas"
              value={String(data?.quantidadeAtivas ?? 0)}
            />
          )}
        </Grid>
        <Grid item xs={6} sm={3}>
          {statsLoading ? (
            <Skeleton variant="rounded" height={72} sx={{ borderRadius: 2 }} />
          ) : (
            <StatMini
              label="Saldo consolidado"
              value={formatarMoedaBRL(data?.saldoConsolidado ?? 0)}
            />
          )}
        </Grid>
      </Grid>

      {data?.efiSandbox ? (
        <Alert severity="info">
          Ambiente de homologação Efí: saldo da API pode não refletir os webhooks
          registrados no extrato interno.
        </Alert>
      ) : null}

      {error ? (
        <FinanceiroErrorState message={error} onRetry={refetch} />
      ) : null}

      {!error && loading && !data ? (
        <Grid container spacing={2}>
          {[1, 2].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : null}

      {!error && data && data.contas.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <AccountBalanceWalletOutlinedIcon
            sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
          />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhuma conta cadastrada
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Crie a primeira conta bancária, caixa ou gateway para registrar
            movimentações e baixas.
          </MDTypography>
        </MDBox>
      ) : null}

      {!error && data && data.contas.length > 0 ? (
        <>
          {totalDestaque > 0 ? (
            <>
              <SectionToolbar title="Contas principais" />
              <Grid container spacing={1.5} alignItems="stretch">
                {contaEfi ? (
                  <Grid item xs={12} md={6} sx={gridCardItemSx}>
                    <ContaFinanceiraCard
                      conta={contaEfi}
                      destaque="efi"
                      onEditar={gerencial.abrirEditar}
                      onMarcarPadrao={gerencial.marcarPadrao}
                      onAlternarAtivo={gerencial.alternarAtivo}
                      onTransferir={
                        podeTransferir ? gerencial.abrirTransferencia : undefined
                      }
                      onRepasseCaixa={gerencial.abrirRepasseCaixa}
                      onConciliarSaldo={
                        data.efiSandbox ? gerencial.abrirConciliacao : undefined
                      }
                      onExcluir={gerencial.abrirExcluir}
                      podeVerSaldoEfi={gerencial.podeVerSaldoEfi}
                      efiSandbox={data.efiSandbox}
                      onAtualizarSaldoEfi={gerencial.atualizarSaldoEfi}
                      onConciliarSaldoEfi={gerencial.conciliarSaldoEfi}
                      atualizandoSaldoEfi={gerencial.atualizandoSaldoEfi}
                      conciliandoSaldoEfi={gerencial.conciliandoSaldoEfi}
                    />
                  </Grid>
                ) : null}
                {contasRepasse.map((c) => (
                  <Grid item xs={12} md={6} key={c.id} sx={gridCardItemSx}>
                    <ContaFinanceiraCard conta={c} {...cardRepasseProps} />
                  </Grid>
                ))}
              </Grid>
            </>
          ) : null}

          <SectionToolbar title="Outras contas" />
          <Grid container spacing={2} alignItems="stretch">
            {outrasContas.map((c) => (
              <Grid item xs={12} sm={6} md={4} key={c.id} sx={gridCardItemSx}>
                <ContaFinanceiraCard
                  conta={c}
                  onEditar={gerencial.abrirEditar}
                  onMarcarPadrao={gerencial.marcarPadrao}
                  onAlternarAtivo={gerencial.alternarAtivo}
                  onTransferir={
                    podeTransferir ? gerencial.abrirTransferencia : undefined
                  }
                  onConciliarSaldo={
                    !isContaGatewayEfi(c) ? gerencial.abrirConciliacao : undefined
                  }
                  onExcluir={gerencial.abrirExcluir}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6} md={4} sx={gridCardItemSx}>
              <CardAdicionar onClick={gerencial.abrirNova} />
            </Grid>
          </Grid>

          {contasInativas.length > 0 ? (
            <Box>
              <MDTypography
                variant="button"
                fontWeight="medium"
                color="text.secondary"
                sx={{ mb: 1.5, display: "block" }}
              >
                Contas inativas
              </MDTypography>
              <Grid container spacing={2} alignItems="stretch">
                {contasInativas.map((c) => (
                  <Grid item xs={12} sm={6} md={4} key={c.id} sx={gridCardItemSx}>
                    <ContaFinanceiraCard
                      conta={c}
                      onEditar={gerencial.abrirEditar}
                      onMarcarPadrao={gerencial.marcarPadrao}
                      onAlternarAtivo={gerencial.alternarAtivo}
                      onExcluir={gerencial.abrirExcluir}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : null}
        </>
      ) : null}

      <ContaFinanceiraFormDialog
        open={gerencial.dialogOpen}
        editMode={Boolean(gerencial.editId)}
        form={gerencial.form}
        setForm={gerencial.setForm}
        loading={gerencial.salvando}
        error={gerencial.dialogError}
        onClose={gerencial.fecharDialog}
        onSave={() => void gerencial.salvar()}
      />

      <ExcluirContaFinanceiraDialog
        state={gerencial.excluirDialog}
        loading={gerencial.excluindo}
        error={gerencial.excluirDialogError}
        onClose={gerencial.fecharExcluir}
        onConfirm={() => void gerencial.confirmarExcluir()}
      />

      <TransferenciaContaFinanceiraDialog
        state={{
          ...gerencial.transferenciaDialog,
          contaOrigem: contaOrigemTransferencia,
        }}
        contasDestino={gerencial.contasDestinoTransferencia}
        form={gerencial.transferenciaForm}
        setForm={gerencial.setTransferenciaForm}
        loading={gerencial.transferindo}
        error={gerencial.transferenciaError}
        onClose={gerencial.fecharTransferencia}
        onConfirm={() => void gerencial.confirmarTransferencia()}
      />

      <ConciliarSaldoContaDialog
        state={{
          ...gerencial.conciliacaoDialog,
          conta: contaConciliacao,
        }}
        form={gerencial.conciliacaoForm}
        setForm={gerencial.setConciliacaoForm}
        loading={gerencial.conciliando}
        error={gerencial.conciliacaoError}
        onClose={gerencial.fecharConciliacao}
        onConfirm={() => void gerencial.confirmarConciliacao()}
      />
    </Stack>
  );
}
